import { Link } from 'react-router-dom'
import { ArrowRight, MessageCircleQuestion } from 'lucide-react'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/button'
import { AnimateIn } from '@/components/motion/AnimateIn'
import { FAQAccordion } from '@/components/faq/FAQAccordion'
import { SECTION_PY } from '@/lib/layoutSpacing'
import { PATHS } from '@/routes/paths'
import type { FAQ } from '@/types/home'
import { cn } from '@/lib/utils'

export function HomeFAQ({ faqs }: { faqs: FAQ[] }) {
  if (faqs.length === 0) return null

  return (
    <section className={cn('relative overflow-hidden bg-linear-to-br from-primary-50 via-[#f0f7ff] to-sky-50', SECTION_PY)}>
      <div
        className="pointer-events-none absolute -left-20 top-0 h-80 w-80 rounded-full bg-primary-200/25 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-10 bottom-0 h-72 w-72 rounded-full bg-sky-200/30 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-100/20 blur-3xl"
        aria-hidden
      />

      <Container className="relative">
        <AnimateIn>
          <div className="mx-auto max-w-6xl">
            <div className="mb-3 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
              <div className="max-w-2xl">
            
                <h2 className="font-display text-lg font-bold tracking-tight text-neutral-900 md:text-3xl">
                  Frequently Asked{' '}
                  <span className="bg-linear-to-r from-primary-600 to-sky-600 bg-clip-text text-transparent">
                    Questions
                  </span>
                </h2>
                <p className="mt-1.5 text-xs leading-relaxed text-neutral-600 md:mt-2 md:text-base">
                  Quick answers about listing and buying on QBC
                </p>
              </div>

              <Button
                asChild
                size="sm"
                className="hidden shrink-0 bg-linear-to-r from-primary-600 to-primary-500 text-white shadow-md shadow-primary-500/25 hover:from-primary-700 hover:to-primary-600 sm:inline-flex"
              >
                <Link to={PATHS.faq}>
                  View all FAQs <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <FAQAccordion
              faqs={faqs}
              defaultOpenIndex={null}
              collapseAllOnMobile
              variant="color-grid"
              columns={2}
            />

            <div className="mt-5 sm:hidden">
              <Button
                asChild
                size="sm"
                className="w-full bg-linear-to-r from-primary-600 to-primary-500 text-white shadow-md"
              >
                <Link to={PATHS.faq}>
                  View all FAQs <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="mt-6 flex items-center justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/90 px-3.5 py-2 text-sm text-neutral-600 shadow-sm backdrop-blur-sm">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-linear-to-br from-primary-500 to-sky-500 text-white">
                  <MessageCircleQuestion className="h-4 w-4" />
                </span>
                <span>
                  Can&apos;t find what you need?{' '}
                  <Link to={PATHS.contact} className="font-semibold text-primary-600 hover:text-primary-700 hover:underline">
                    Contact our team
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </AnimateIn>
      </Container>
    </section>
  )
}
